import { TeamDetails } from "../../../components/crm/customer/types";
import { ExtendedTemplate } from "../../marketing-pages/create-campaign/types";
import { TemplateModule } from "../../marketing-pages/templates";
import { ContactItem } from "../customers/types";
import { TicketProps } from "../ticket/types";
import { ChatMessage, Conversation, LastMessage } from "./types";

import { ChannelConfig } from "./types";
import {
  FaSearch,
  FaPhone,
  FaEllipsisV,
  FaPaperPlane,
  FaMicrophone,
  FaPaperclip,
  FaChevronDown,
  FaRobot,
  FaWhatsapp,
  FaSms,
  FaEnvelope,
  FaLinkedin,
  FaPhoneAlt,
  FaUsers,
  FaArrowRight,
  FaPlus,
  FaBolt,
  FaClock,
  FaComment,
  FaVideo,
  FaImage
} from "react-icons/fa";
import { Sparkle, Eye, MoreVertical } from "lucide-react";
import { RiMessage2Line } from "react-icons/ri";
import { JSX } from "react";

export const generateIdempotencyKey = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

export const getContactDisplayName = (contact?: ContactItem | null): string => {
  if (!contact) return "N.A.";
  const firstName = contact.first_name || contact.firstName || "";
  const lastName = contact.last_name || contact.lastName || "";
  return `${firstName} ${lastName}`.trim() || contact.email || contact.phone || "N.A.";
};


export const getContactSource = (contact?: ContactItem | null): string =>
  String(contact?.custom?.source || contact?.custom?.lead_source || contact?.custom?.channel || "N.A.");

export const getContactLocation = (contact?: ContactItem | null): string => {
  const parts = [
    contact?.custom?.city,
    contact?.custom?.state,
    contact?.custom?.country,
  ].filter(Boolean);
  return parts.length ? parts.join(", ") : "N.A.";
};

export const getFirstContactDate = (contact?: ContactItem | null): string => {
  const rawDate = contact?.custom?.created_at || contact?.updated_at || contact?.updatedAt;
  if (!rawDate || typeof rawDate !== "string") return "N.A.";
  return new Date(rawDate).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export const getContactPriority = (contact?: ContactItem | null): string =>
  String(contact?.custom?.priority || contact?.custom?.lead_priority || "N.A.");

export const getTeamMemberName = (member?: TeamDetails | null): string =>
  member ? `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email : "";

export const getAssignedTo = (ticket?: TicketProps | null, team: TeamDetails[] = []): string => {
  const assignedName =
    (ticket as any)?.assigned_to_user_name ||
    (ticket as any)?.assigned_user_name ||
    (ticket as any)?.assignee_name ||
    (ticket as any)?.assigned_to_user?.name ||
    getTeamMemberName((ticket as any)?.assigned_to_user) ||
    getTeamMemberName((ticket as any)?.assigned_user) ||
    getTeamMemberName((ticket as any)?.assignee);

  if (assignedName) return assignedName;

  const assignedId = ticket?.assigned_to_user_id;
  const teamMember = team.find((member) => member.id === assignedId);
  return getTeamMemberName(teamMember) || (assignedId ? "Assigned" : "Unassigned");
};

export const getFirstContactFromResponse = (response: any): ContactItem | null => {
  if (Array.isArray(response)) return response[0] ?? null;
  if (Array.isArray(response?.results)) return response.results[0] ?? null;
  if (Array.isArray(response?.data)) return response.data[0] ?? null;
  if (Array.isArray(response?.contacts)) return response.contacts[0] ?? null;
  return response?.id ? response : null;
};

export const getFirstTicketFromResponse = (response: any): TicketProps | null => {
  if (Array.isArray(response)) return response[0] ?? null;
  if (Array.isArray(response?.results)) return response.results[0] ?? null;
  if (Array.isArray(response?.data)) return response.data[0] ?? null;
  if (Array.isArray(response?.tickets)) return response.tickets[0] ?? null;
  return response?.id ? response : null;
};

export const getFirstNameSearchTerm = (value: string): string =>
  value
    ?.trim()
    ?.split(/\s+/)[0]
    ?.replace(/[^\p{L}\p{N}+@._-]/gu, "") || "";

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const restoreTemplatePlaceholders = (
  content: string,
  variables: TemplateModule["variables"] = []
): string => {
  if (!content || /\{\{\d+\}\}/.test(content)) return content;

  return variables.reduce((body, variable) => {
    const example = variable?.example?.trim();
    if (!example || !variable?.index) return body;

    return body.replace(
      new RegExp(`\\b${escapeRegExp(example)}\\b`, "g"),
      `{{${variable.index}}}`
    );
  }, content);
};

// ─────────────────────────────────────────────────────────────
// Enhanced Template Renderer with Contact Auto-fill
// ─────────────────────────────────────────────────────────────

export const renderTemplateWithVariableValues = (
  template: ExtendedTemplate,
  contact?: ContactItem | null,
  customValues: Record<string, string> = {}
): string => {
  if (!template?.originalContent) return "";

  let rendered = template.originalContent;

  const getValueForVariable = (varContent: string): string => {
    const key = varContent.trim();
    const lowerKey = key.toLowerCase();

    // 1. Custom overrides (if user edited)
    if (customValues[key] !== undefined) return customValues[key];

    if (contact) {
      const fullName = getContactDisplayName(contact);
      const firstName = fullName.split(" ")[0] || fullName;

      // 2. Smart matching by variable name
      const varDef = template.variables?.find(v => 
        String(v.index) === key || 
        v.name?.toLowerCase() === lowerKey
      );

      if (varDef) {
        const varName = varDef.name?.toLowerCase() || "";

        // Meaningful names
        if (["name", "fullname", "customer_name", "contact_name"].includes(varName)) {
          return fullName;
        }
        if (["first_name", "firstname", "first"].includes(varName)) {
          return firstName;
        }
        if (["last_name", "lastname", "last"].includes(varName)) {
          return fullName.split(" ").slice(1).join(" ") || "";
        }
        if (["phone", "mobile", "whatsapp"].includes(varName)) {
          return contact.phone || "";
        }
        if (["email"].includes(varName)) {
          return contact.email || "";
        }
      }

      // 3. Fallback for generic variable names (var_1, variable1, param1, etc.)
     
      if (lowerKey === "1" || lowerKey === "var_1" || lowerKey === "variable1" || 
          lowerKey.startsWith("var_") || lowerKey.startsWith("param")) {
        return firstName;           // ← This fixes your "bday_wish" template
      }
    }

    // 4. Final fallback to example value
    const variableDef = template.variables?.find(v => 
      String(v.index) === key || v.name?.toLowerCase() === lowerKey
    );

    return variableDef?.example || `{{${key}}}`;
  };

  // Replace all {{ }} placeholders
  rendered = rendered.replace(/\{\{([^}]+)\}\}/g, (_, varContent) => {
    return getValueForVariable(varContent);
  });

  return rendered;
};

export const getTemplateVariableValues = (template: ExtendedTemplate): Record<string, string> =>
  (template.variables || []).reduce((acc, variable) => {
    const value = variable.example || variable.name;
    if (value) acc[String(variable.index)] = value;
    return acc;
  }, {} as Record<string, string>);

const toNumber = (value: unknown): number => {
  const count = Number(value);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

export const getConversationUnreadCount = (conversation: any): number =>
  toNumber(
    conversation?.unread_count ??
    conversation?.unreadCount ??
    conversation?.unread ??
    conversation?.unread_messages_count ??
    conversation?.unreadMessagesCount ??
    conversation?.unread_chat_count
  );

// Helper to safely extract LastMessage object
const getConversationLastMessage = (conv: any): LastMessage | undefined => {
  if (!conv.last_message) return undefined;

  // If it's already a proper object
  if (typeof conv.last_message === 'object' && conv.last_message.body) {
    return {
      id: conv.last_message.id || `msg_${Date.now()}`,
      direction: conv.last_message.direction || "outbound",
      body: conv.last_message.body || "",
      type: conv.last_message.type || "text",
      status: conv.last_message.status || "delivered",
      created_at: conv.last_message.created_at || conv.last_message_at,
    };
  }

  // If it's a string (fallback)
  if (typeof conv.last_message === 'string') {
    return {
      id: `msg_${Date.now()}`,
      direction: "outbound",
      body: conv.last_message,
      type: "text",
      status: "delivered",
      created_at: conv.last_message_at,
    };
  }

  return undefined;
};



export const getRealtimeConversationId = (message?: Partial<ChatMessage> | null, fallback?: string | null): string =>
  String(
    fallback ||
    message?.conversation_id ||
    (message as any)?.conversationId ||
    (message as any)?.conversation ||
    ""
  );

export const isSameConversationId = (left?: string | null, right?: string | null): boolean =>
  !!left && !!right && String(left) === String(right);

export const isInboundDirection = (direction?: string | null): boolean =>
  ["inbound", "incoming", "received"].includes(String(direction || "").toLowerCase());

export const normalizeConversation = (conversation: Conversation): Conversation => ({
  ...conversation,
  last_message: getConversationLastMessage(conversation),
  unread_count: getConversationUnreadCount(conversation),
});

export const Icons = {
  Search: () => <FaSearch size={16} />,
  Phone: ({ size = 16 }: { size?: number }) => <FaPhone size={size} />,
  MoreVert: () => <FaEllipsisV size={16} />,
  MoreVertical: ({ size = 16 }: { size?: number }) => <MoreVertical size={size} />,
  Send: ({ size = 16 }: { size?: number }) => <FaPaperPlane size={size} />,
  Mic: () => <FaMicrophone size={16} />,
  Attach: () => <FaPaperclip size={16} />,
  Paperclip: ({ size = 16 }: { size?: number }) => <FaPaperclip size={size} />,
  ChevronDown: ({ size = 16 }: { size?: number }) => <FaChevronDown size={size} />,
  Bot: () => <FaRobot size={16} />,
  Whatsapp: () => <FaWhatsapp size={16} color="#25D366" />,
  SMS: ({ size = 16 }: { size?: number }) => <FaSms size={size} />,
  Email: ({ size = 16 }: { size?: number }) => <FaEnvelope size={size} />,
  Linkedin: () => <FaLinkedin size={16} color="#0077B5" />,
  Voice: ({ size = 16 }: { size?: number }) => <FaPhoneAlt size={size} />,
  Agents: ({ size = 16 }: { size?: number }) => <FaUsers size={size} />,
  ArrowRight: ({ size = 16 }: { size?: number }) => <FaArrowRight size={size} />,
  Plus: ({ size = 16 }: { size?: number }) => <FaPlus size={size} />,
  Zap: ({ size = 16 }: { size?: number }) => <FaBolt size={size} />,
  Clock: () => <FaClock size={16} />,
  Comment: () => <FaComment size={16} />,
  Sparkles: () => <Sparkle size={16} />,
  Eye: ({ size = 16 }: { size?: number }) => <Eye size={size} />,
  Video: ({ size = 16 }: { size?: number }) => <FaVideo size={size} />,
  Image: ({ size = 16 }: { size?: number }) => <FaImage size={size} />,
  RCS: ({ size = 16 }: { size?: number }) => <RiMessage2Line size={size} color="#5E35B1" />,
};

export const getChannelConfigs = (whatsappCount: number, teamCount: number): ChannelConfig[] => [
  { 
    label: "All Channels", 
    count: whatsappCount, 
    icon: <span className="text-xl">⊞</span>, 
    channelType: "whatsapp" 
  },
  { 
    label: "WhatsApp", 
    count: whatsappCount, 
    icon: <Icons.Whatsapp />, 
    channelType: "whatsapp" 
  },
  { 
    label: "SMS", 
    count: 0, 
    icon: <Icons.SMS />, 
    channelType: "sms", 
    isComingSoon: true 
  },
  { 
    label: "Email", 
    count: 0, 
    icon: <Icons.Email />, 
    channelType: "email", 
    isComingSoon: true 
  },
  {
    label: "RCS",
    count: 0,
    icon: <Icons.RCS />,
    channelType: "rcs"
  },
  { 
    label: "LinkedIn", 
    count: 0, 
    icon: <Icons.Linkedin />, 
    channelType: "linkedin", 
    isComingSoon: true 
  },
  { 
    label: "Voice", 
    count: 0, 
    icon: <Icons.Voice />, 
    channelType: "voice", 
    isComingSoon: true 
  },
  { 
    label: "Agents", 
    count: teamCount || 0, 
    icon: <Icons.Agents />, 
    channelType: "agents", 
    isComingSoon: true 
  },
];

export const getMessageStatusIcon = (status: string, direction: string): JSX.Element | null => {
  if (direction !== "outbound") return null;

  switch (status) {
    case "sent":
      return <span className="text-blue-400 text-xs">✓</span>;
    case "delivered":
      return <span className="text-blue-400 text-xs">✓✓</span>;
    case "read":
      return <span className="text-blue-600 text-xs">✓✓</span>;
    case "failed":
      return <span className="text-red-500 text-xs font-medium" title="Failed to send">⚠ Failed</span>;
    default:
      return <span className="text-slate-400 text-xs">✓</span>;
  }
};

export const serviceFlowSteps: string[] = ["Address Collection", "Schedule Visit", "Work Order Creation", "Payment Confirmation", "Technician Assignment", "Confirm Completion", "Close Job"];
export const statusPipelineSteps: string[] = ["New Lead", "Potential First Order", "Reminders", "Recurring Orders", "Qualified"];
